import type { StufenBeschreibung } from '@luzula/luzula-lib';

/**
 * Skelettgehaltsstufen nach SEA 95, Teil B, Darst. B 3-15 („Gehalt an Stein und
 * Kies in Volumen-%", S. 73).
 *
 * Bezugsbasis: **Volumen-%** der Lage — nicht Masse-% wie beim Humusgehalt.
 *
 * Der Eingabewert ist die Ziffer des Kurzzeichens: 0 = st 0 / ki 0, 7 = st 7 /
 * ki 7. Stein und Kies teilen sich die Reihe; welches von beidem vorliegt, sagt
 * das Kurzzeichen, nicht die Stufe.
 *
 * `mittel` ist der Wert, mit dem die App rechnet: `schichtNWSKEval` zieht ihn
 * als Prozentsatz vom Wasserspeicher ab. Er steht NICHT in der SEA — die gibt
 * nur die Spannen an. Er kommt aus der nWSK-Rechnung (Konopatzky 1998), wie sie
 * auch in der Import-Vorlage `assets/xlsx/Bohrpunkt.xlsx` (Blatt GRLG) steht,
 * und ist dort der Mittelwert der Spanne.
 */
export interface SkelettStufe {
	/** Eingabewert in der Substratfolge (0 … 7). */
	wert: number;
	/** Kurzzeichen für Stein. */
	stein: string;
	/** Kurzzeichen für Kies. */
	kies: string;
	/** Spanne in Volumen-%. */
	spanne: string;
	bezeichnung: string;
	/** Mittelwert der Spanne in Volumen-% — damit rechnet der Abschlag. */
	mittel: number;
}

export const SKELETT_STUFEN: SkelettStufe[] = [
	{ wert: 0, stein: 'st 0', kies: 'ki 0', spanne: '0', bezeichnung: 'stein- oder kiesfrei (skelettfrei)', mittel: 0 },
	{ wert: 1, stein: 'st 1', kies: 'ki 1', spanne: '0,1 … 2', bezeichnung: 'sehr schwach steinig oder kiesig', mittel: 1.05 },
	{ wert: 2, stein: 'st 2', kies: 'ki 2', spanne: '> 2 … 5', bezeichnung: 'schwach steinig oder kiesig', mittel: 3.5 },
	{ wert: 3, stein: 'st 3', kies: 'ki 3', spanne: '> 5 … 10', bezeichnung: 'ziemlich schwach steinig oder kiesig', mittel: 7.5 },
	{ wert: 4, stein: 'st 4', kies: 'ki 4', spanne: '> 10 … 25', bezeichnung: 'mäßig steinig oder kiesig', mittel: 17.5 },
	{ wert: 5, stein: 'st 5', kies: 'ki 5', spanne: '> 25 … 50', bezeichnung: 'stark steinig oder kiesig', mittel: 37.5 },
	{ wert: 6, stein: 'st 6', kies: 'ki 6', spanne: '> 50 … 75', bezeichnung: 'sehr stark steinig oder kiesig', mittel: 62.5 },
	{ wert: 7, stein: 'st 7', kies: 'ki 7', spanne: '> 75', bezeichnung: 'Skelett (-boden)', mittel: 87.5 },
];

/**
 * Von hell nach dunkel wie die Steindichte, Eingabewert = Index — nur zur
 * Orientierung. Färbt das Hilfefenster (Legende) UND das Feld in der
 * Substrattabelle; eine Reihe, nicht zwei.
 */
export const SKELETT_FARBEN: readonly string[] = [
	'#ffffff', '#eceff1', '#d7dde0', '#c0c8cd', '#a5b0b6', '#87949c', '#6a7880', '#4e5b63',
];

/** Die Stufen für das Stufen-Feld (`wd-stufen-input`): Wert, Farbe, Name. */
export const SKELETT_STUFEN_FELD: StufenBeschreibung[] = SKELETT_STUFEN.map(s => ({
	wert: s.wert, farbe: SKELETT_FARBEN[s.wert], name: s.bezeichnung,
}));

/**
 * Zusatz-Kurzzeichen aus derselben Darstellung: Stein und Kies dürfen
 * zusammengefasst werden, und für kantige Skelettkörper gibt es ein eigenes
 * Zeichen.
 */
export const SKELETT_ZUSAETZE: { code: string; bedeutung: string }[] = [
	{ code: 'sk', bedeutung: 'skeletthaltig — Stein- und Kiesgehalt zusammengefasst' },
	{ code: 'gr', bedeutung: 'grusig — kantige Skelettkörper' },
];
